import { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import {
  getDownloadURL,
  listAll,
  ref,
  uploadBytesResumable,
} from "firebase/storage";
import { db, storage } from "../firebase/firebase";

import { AuthContext } from "../context/AuthContext";
import UserManagement from "../components/UserManagement";
import GroupManagement from "../components/GroupManagement";

import "../styles/AdminTools.css";

const MAX_FILES = 10;

function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

function formatSeconds(sec) {
  if (!isFinite(sec) || sec < 0) return "—";
  if (sec < 60) return `${Math.round(sec)}s`;
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}m ${s}s`;
}

export default function AdminDashboard({ onBackClick }) {
  const { userData, authLoading } = useContext(AuthContext);

  // Role checks (safe even if userData missing)
  const role = userData?.role || "user";
  const canManageUsers = role === "admin";
  const canManageResources = role === "admin" || role === "uploader";

  const [groups, setGroups] = useState([]);
  const [folders, setFolders] = useState([]); // includes subfolders

  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const [folderName, setFolderName] = useState("");
  const [parentId, setParentId] = useState(""); // "" = top-level
  const [allowedGroups, setAllowedGroups] = useState([]);

  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [folderDocs, setFolderDocs] = useState([]);

  // Drag/drop upload state
  const [files, setFiles] = useState([]); // File[]
  const [uploading, setUploading] = useState(false);
  const [uploads, setUploads] = useState([]); // [{name, progress, status, bytesTransferred, totalBytes, speedBps, etaSec}]
  const uploadStartRef = useRef({}); // { [fileName]: startTimeMs }

  useEffect(() => {
    // Only load what user is allowed to see.
    // Resource managers need folders & storage previews. Users shouldn't be here anyway.
    if (!authLoading && canManageResources) {
      loadAll().catch(console.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, canManageResources]);

  const loadAll = async () => {
    const tasks = [
      getDocs(collection(db, "folders")),
    ];

    // Only admins can read groups if your rules restrict it.
    // If your rules allow uploader read, you can remove this guard.
    if (canManageUsers) tasks.push(getDocs(collection(db, "groups")));

    const results = await Promise.all(tasks);

    const foldersSnap = results[0];
    setFolders(foldersSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    if (canManageUsers) {
      const groupsSnap = results[1];
      setGroups(groupsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } else {
      setGroups([]); // uploader doesn’t need to assign groups (optional)
    }
  };

  const topFolders = useMemo(() => folders.filter((f) => !f.parentId), [folders]);
  const childrenOf = (id) => folders.filter((f) => f.parentId === id);

  const toggleAllowedGroup = (name) => {
    setAllowedGroups((prev) =>
      prev.includes(name) ? prev.filter((g) => g !== name) : [...prev, name]
    );
  };

  const createFolder = async () => {
    if (!canManageResources) return alert("You do not have permission to create folders.");
    if (!folderName.trim()) return alert("Enter folder name");

    await addDoc(collection(db, "folders"), {
      name: folderName.trim(),
      parentId: parentId || null,
      allowedGroups: canManageUsers ? allowedGroups : [], // uploader: optional
      createdAt: serverTimestamp(),
    });

    setFolderName("");
    setParentId("");
    setAllowedGroups([]);
    setShowCreateFolder(false);
    await loadAll();
  };

  const deleteFolder = async (id) => {
    if (!canManageResources) return alert("You do not have permission to delete folders.");
    if (!window.confirm("Delete this folder entry? (Storage files will remain.)")) return;
    await deleteDoc(doc(db, "folders", id));
    await loadAll();
  };

  const buildFolderPath = (folderId) => `folders/${folderId}`;

  const loadFolderDocs = async (folderId) => {
    setSelectedFolderId(folderId);
    setFolderDocs([]);
    if (!folderId) return;

    try {
      const folderRef = ref(storage, buildFolderPath(folderId));
      const fileList = await listAll(folderRef);
      const docs = await Promise.all(
        fileList.items.map(async (item) => ({
          name: item.name,
          url: await getDownloadURL(item),
        }))
      );
      setFolderDocs(docs);
    } catch (e) {
      console.error(e);
      setFolderDocs([]);
    }
  };

  // ---------- Drag & Drop helpers ----------
  const addFiles = (incomingFiles) => {
    const incoming = Array.from(incomingFiles || []);
    if (incoming.length === 0) return;

    const merged = [...files, ...incoming];

    // de-dup by (name + size + lastModified)
    const uniq = [];
    const seen = new Set();
    for (const f of merged) {
      const key = `${f.name}|${f.size}|${f.lastModified}`;
      if (!seen.has(key)) {
        seen.add(key);
        uniq.push(f);
      }
    }

    if (uniq.length > MAX_FILES) {
      alert(`You can upload up to ${MAX_FILES} files at a time.`);
      setFiles(uniq.slice(0, MAX_FILES));
      return;
    }

    setFiles(uniq);
  };

  const removeFile = (idx) => {
    setFiles((prev) => prev.filter((_, i) => i !== idx));
  };

  const clearFiles = () => {
    setFiles([]);
  };

  const uploadMany = async () => {
    if (!canManageResources) return alert("You do not have permission to upload files.");
    if (!selectedFolderId) return alert("Select a folder to upload into");
    if (!files.length) return alert("Select one or more files");
    if (files.length > MAX_FILES) return alert(`Max ${MAX_FILES} files allowed`);

    setUploading(true);

    // initialize upload entries
    const initial = files.map((f) => ({
      name: f.name,
      progress: 0,
      status: "queued", // queued | uploading | done | error
      bytesTransferred: 0,
      totalBytes: f.size,
      speedBps: 0,
      etaSec: Infinity,
    }));
    setUploads(initial);

    const folderPath = buildFolderPath(selectedFolderId);

    // Run uploads in parallel (10 max already)
    await Promise.all(
      files.map((file) => {
        return new Promise((resolve) => {
          const storageRef = ref(storage, `${folderPath}/${file.name}`);
          const task = uploadBytesResumable(storageRef, file);

          uploadStartRef.current[file.name] = Date.now();

          task.on(
            "state_changed",
            (snapshot) => {
              const now = Date.now();
              const start = uploadStartRef.current[file.name] || now;
              const dtSec = Math.max(0.2, (now - start) / 1000);
              const speed = snapshot.bytesTransferred / dtSec; // B/s
              const remaining = snapshot.totalBytes - snapshot.bytesTransferred;
              const eta = speed > 0 ? remaining / speed : Infinity;
              const pct =
                snapshot.totalBytes > 0
                  ? (snapshot.bytesTransferred / snapshot.totalBytes) * 100
                  : 0;

              setUploads((prev) =>
                prev.map((u) =>
                  u.name === file.name
                    ? {
                        ...u,
                        status: "uploading",
                        progress: Math.min(100, Math.max(0, pct)),
                        bytesTransferred: snapshot.bytesTransferred,
                        totalBytes: snapshot.totalBytes,
                        speedBps: speed,
                        etaSec: eta,
                      }
                    : u
                )
              );
            },
            (err) => {
              console.error("Upload error:", err);
              setUploads((prev) =>
                prev.map((u) =>
                  u.name === file.name ? { ...u, status: "error" } : u
                )
              );
              resolve(); // continue even if one fails
            },
            () => {
              setUploads((prev) =>
                prev.map((u) =>
                  u.name === file.name
                    ? { ...u, status: "done", progress: 100, etaSec: 0 }
                    : u
                )
              );
              resolve();
            }
          );
        });
      })
    );

    setUploading(false);

    // refresh file list preview
    await loadFolderDocs(selectedFolderId);

    // clear local selection
    clearFiles();
    const input = document.getElementById("multiFileInput");
    if (input) input.value = "";

    alert("Upload complete (check any failed items marked as error).");
  };

  const renderFolderTree = (folder, depth = 0) => {
    const kids = childrenOf(folder.id);
    return (
      <div key={folder.id} style={{ marginLeft: depth ? 14 : 0 }}>
        <div className="admin-folder-row">
          <button
            className={`admin-folder-btn ${
              selectedFolderId === folder.id ? "is-active" : ""
            }`}
            type="button"
            onClick={() => loadFolderDocs(folder.id)}
          >
            {depth ? "↳" : "📁"} {folder.name}
          </button>

          <div className="admin-folder-meta">
            <span className="admin-muted">
              {folder.allowedGroups?.length
                ? `Groups: ${folder.allowedGroups.join(", ")}`
                : "Groups: none"}
            </span>

            {canManageResources && (
              <button
                className="btn btn-secondary"
                type="button"
                onClick={() => deleteFolder(folder.id)}
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {kids.length ? (
          <div style={{ marginTop: 6 }}>
            {kids.map((c) => renderFolderTree(c, depth + 1))}
          </div>
        ) : null}
      </div>
    );
  };

  // ---------- Guard UI ----------
  if (authLoading) {
    return (
      <div className="content-area">
        <div className="admin-muted">Loading…</div>
      </div>
    );
  }

  if (!canManageResources) {
    return (
      <div className="content-area">
        
        <div className="page-header">
          <h1 className="page-title">Resource Panel</h1>
          <p className="page-subtitle">You don’t have permission to access this page.</p>
          <button className="btn btn-secondary" type="button" onClick={onBackClick}>
            ← Back
          </button>
        </div>

        <div className="admin-info">
          <strong>Access required</strong>
          <p className="admin-muted" style={{ marginTop: 8 }}>
            Ask your administrator to assign you the <b>uploader</b> or <b>admin</b> role.
          </p>
        </div>
      </div>
    );
  }

  // ---------- Main UI ----------
  return (
    <div className="content-area admin-shell">
      <div className="page-header">
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1 className="page-title">
              {role === "admin" ? "Admin Dashboard" : "Resource Uploader Panel"}
            </h1>
            <p className="page-subtitle">
              {role === "admin"
                ? "Manage users, groups and resources"
                : "Create folders and upload resources"}
            </p>

            {!userData && (
              <div className="admin-info" style={{ marginTop: 10 }}>
                <strong>Note</strong>
                <p className="admin-muted" style={{ marginTop: 8 }}>
                  Your user profile (role/groups) is not loaded yet. If this persists,
                  check Firestore permissions for <code>users/{`{uid}`}</code>.
                </p>
              </div>
            )}
          </div>

          <button className="btn btn-secondary" type="button" onClick={onBackClick}>
            ← Back
          </button>
        </div>
      </div>

      {/* Only ADMIN sees user/group management */}
      {canManageUsers && <UserManagement />}
      {canManageUsers && <GroupManagement />}

      
    </div>
  );
}
